import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  cancelStickyElementScroll,
  isElementFullyVisibleWithinContainer,
  startStickyElementScroll,
} from '@/components/ui/scroll/stickyElementScroll';
import { onChatSendFromAnyWindow } from '@/features/chat/chat.events';
import { useInViewport } from '@/hooks/useInViewport';
import { afterFrames } from '@/lib/animate';
import { logChatRenderDebug } from '@/utils/chat-render-debug';

import {
  findLastTurnStartId,
  findNewTailTurnStartId,
  resolveLiveStartIndex,
  resolveLiveStartMessageIdForSendEvent,
  shouldDeactivateLiveRegion,
} from './messageListLiveTurn';
import { CHAT_THREAD_ID } from './useChatScroll';

interface MessageLike {
  id: string;
  role: string;
  joinedTurn?: boolean;
}

interface LiveStartItemLike {
  type: string;
  message?: {
    id: string;
  };
}

interface UseLiveTurnRegionOptions {
  threadId?: string;
  messages: MessageLike[];
  chatItems: LiveStartItemLike[];
  isBusy: boolean;
  isResponding: boolean;
  threadContainerRef: React.RefObject<HTMLDivElement | null>;
  isAtTailRef: React.RefObject<boolean>;
  userHasScrolledRef: React.MutableRefObject<boolean>;
}

interface UseLiveTurnRegionResult {
  liveStartIndex: number | null;
  isLive: boolean;
  isLiveInView: boolean;
  liveRegionRef: React.RefObject<HTMLDivElement | null>;
  jumpToLiveRegion: (options?: ScrollIntoViewOptions) => void;
  liveBufferRef: (node: HTMLDivElement | null) => void;
  showLiveBuffer: boolean;
}

export function useLiveTurnRegion({
  threadId,
  messages,
  chatItems,
  isBusy,
  isResponding,
  threadContainerRef,
  isAtTailRef,
  userHasScrolledRef,
}: UseLiveTurnRegionOptions): UseLiveTurnRegionResult {
  const [liveStartMessageId, setLiveStartMessageId] = useState<string | null>(
    null
  );
  const pendingLiveActivationRef = useRef<{
    anchorMessageId: string | null;
    messageCountAtEvent: number;
  } | null>(null);
  const [pendingLiveActivationVersion, setPendingLiveActivationVersion] =
    useState(0);
  const [liveOffScreen, setLiveOffScreen] = useState(false);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const jumpFollowUpRafId = useRef<number | null>(null);

  const liveStartIndex = useMemo(
    () => resolveLiveStartIndex(chatItems, liveStartMessageId),
    [chatItems, liveStartMessageId]
  );
  const isLive = liveStartIndex !== null;

  // Debug: a flip to null means the anchor vanished and the live items
  // migrated back into the VList.
  const prevLiveStartIndexRef = useRef<number | null>(liveStartIndex);
  useEffect(() => {
    if (prevLiveStartIndexRef.current !== liveStartIndex) {
      logChatRenderDebug('live-start-index-change', {
        from: prevLiveStartIndexRef.current,
        to: liveStartIndex,
        liveStartMessageId,
        chatItemCount: chatItems.length,
      });
      prevLiveStartIndexRef.current = liveStartIndex;
    }
  }, [liveStartIndex, liveStartMessageId, chatItems.length]);
  const isLiveInView = isLive && !liveOffScreen;

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const jumpToLiveRegion = useCallback(
    (options?: ScrollIntoViewOptions) => {
      const liveRegion = liveRegionRef.current;
      if (!liveRegion) return;

      logChatRenderDebug('jump-to-live-region', {
        behavior: options?.behavior ?? 'smooth',
      });

      const resolvedOptions: ScrollIntoViewOptions = {
        behavior: 'smooth',
        // 'start' causes scrollIntoView to scroll the body in HB — it scrolls all
        // overflow:hidden ancestors, and the ChatHeader offset forces a body scroll.
        // 'center' avoids this since the VList center aligns with the body center.
        block: 'center',
        inline: 'center',
        ...options,
      };

      const scrollContainer = getThreadScrollContainer(
        threadContainerRef,
        threadId
      );
      if (!scrollContainer) {
        liveRegion.scrollIntoView(resolvedOptions);
        return;
      }

      // When already in the live region viewport, a direct smooth jump is enough
      if (
        resolvedOptions.behavior !== 'smooth' ||
        isElementFullyVisibleWithinContainer(scrollContainer, liveRegion)
      ) {
        liveRegion.scrollIntoView(resolvedOptions);
        return;
      }

      startStickyElementScroll({
        rafIdRef: jumpFollowUpRafId,
        scrollContainer,
        targetElement: liveRegion,
        scrollIntoViewOptions: resolvedOptions,
      });
    },
    [threadContainerRef, threadId]
  );

  useEffect(() => () => cancelStickyElementScroll(jumpFollowUpRafId), []);

  useEffect(() => {
    if (!threadId) return;

    const listener = onChatSendFromAnyWindow((sentThreadId) => {
      logChatRenderDebug('live-send-event', {
        eventThreadId: sentThreadId,
        threadId,
        matches: sentThreadId === threadId,
        messageCount: messagesRef.current.length,
      });
      if (sentThreadId !== threadId) return;
      const currentMessages = messagesRef.current;
      pendingLiveActivationRef.current = {
        anchorMessageId: findLastTurnStartId(currentMessages),
        messageCountAtEvent: currentMessages.length,
      };
      setPendingLiveActivationVersion((version) => version + 1);
    });

    return () => listener.unsubscribe();
  }, [threadId]);

  useEffect(() => {
    const pending = pendingLiveActivationRef.current;
    if (!pending) return;

    const nextLiveStartMessageId = resolveLiveStartMessageIdForSendEvent(
      messages,
      pending.anchorMessageId,
      pending.messageCountAtEvent
    );

    if (!nextLiveStartMessageId) {
      logChatRenderDebug('live-resolve-null', {
        anchorMessageId: pending.anchorMessageId,
        messageCountAtEvent: pending.messageCountAtEvent,
        currentCount: messages.length,
        lastRole: messages[messages.length - 1]?.role ?? null,
        lastId: messages[messages.length - 1]?.id ?? null,
      });
      return;
    }

    pendingLiveActivationRef.current = null;
    logChatRenderDebug('live-activate', {
      anchorMessageId: nextLiveStartMessageId,
    });
    setLiveStartMessageId(nextLiveStartMessageId);
    setLiveOffScreen(false);
  }, [messages, pendingLiveActivationVersion]);

  useEffect(() => {
    setLiveStartMessageId(null);
    pendingLiveActivationRef.current = null;
    setLiveOffScreen(false);
  }, [threadId]);

  const lastTurnStartId = findLastTurnStartId(messages);
  const trackedTailRef = useRef({ threadId, messageId: lastTurnStartId });
  useLayoutEffect(() => {
    const trackedTail = trackedTailRef.current;
    if (
      trackedTail.threadId === threadId &&
      trackedTail.messageId === lastTurnStartId
    ) {
      return;
    }
    trackedTailRef.current = { threadId, messageId: lastTurnStartId };
    const newTailTurnStartId =
      trackedTail.threadId === threadId
        ? findNewTailTurnStartId(messages, trackedTail.messageId)
        : null;
    const isFollowingThread =
      isAtTailRef.current || !userHasScrolledRef.current;
    if (!newTailTurnStartId || !isFollowingThread) return;

    userHasScrolledRef.current = false;
    pendingLiveActivationRef.current = null;
    logChatRenderDebug('live-follow-new-turn', {
      anchorMessageId: newTailTurnStartId,
    });
    setLiveStartMessageId(newTailTurnStartId);
    setLiveOffScreen(false);
  }, [threadId, lastTurnStartId, messages, isAtTailRef, userHasScrolledRef]);

  // When the live region activates, items move from full-height VList entries
  // to zero-height placeholders (rendered in the footer instead). This height
  // change causes VList to recompute scroll position before the browser
  // paints, producing a visible scroll jump. Pin to bottom synchronously
  // via useLayoutEffect so the user never sees the intermediate state.
  const prevIsLiveRef = useRef(isLive);
  useLayoutEffect(() => {
    const wasLive = prevIsLiveRef.current;
    prevIsLiveRef.current = isLive;
    if (!isLive || wasLive) return;

    // isLive just transitioned false → true
    const scrollContainer = getThreadScrollContainer(
      threadContainerRef,
      threadId
    );
    if (scrollContainer) {
      logChatRenderDebug('live-pin-bottom', {
        fromScrollTop: Math.round(scrollContainer.scrollTop),
        toScrollTop: Math.round(
          scrollContainer.scrollHeight - scrollContainer.clientHeight
        ),
      });
      scrollContainer.scrollTop =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
    }
  });

  useEffect(() => {
    if (!isLive) return;
    afterFrames(5, () => jumpToLiveRegion());
  }, [isLive, jumpToLiveRegion, liveStartMessageId]);

  useEffect(() => {
    if (!isLive) return;

    const liveRegion = liveRegionRef.current;
    const scrollContainer = getThreadScrollContainer(
      threadContainerRef,
      threadId
    );
    if (!liveRegion || !scrollContainer) return;

    const io = new IntersectionObserver(
      ([entry]) => setLiveOffScreen(!entry.isIntersecting),
      { root: scrollContainer, threshold: 0 }
    );
    io.observe(liveRegion);

    return () => io.disconnect();
  }, [isLive, threadContainerRef, threadId]);

  useEffect(() => {
    if (!liveStartMessageId) return;
    if (!shouldDeactivateLiveRegion(isBusy, liveOffScreen)) return;

    // The IntersectionObserver can report stale off-screen state after
    // layout changes (e.g., items moving from footer to VList children
    // on a new send). Verify with a direct bounds check before
    // deactivating — deactivation is irreversible and causes a visible
    // layout shift if the live region is still on-screen.
    const liveRegion = liveRegionRef.current;
    const scrollContainer = getThreadScrollContainer(
      threadContainerRef,
      threadId
    );
    if (liveRegion && scrollContainer) {
      const regionRect = liveRegion.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const isActuallyVisible =
        regionRect.bottom > containerRect.top &&
        regionRect.top < containerRect.bottom;
      if (isActuallyVisible) {
        logChatRenderDebug('live-deactivate-rescued', {});
        setLiveOffScreen(false);
        return;
      }
    }

    logChatRenderDebug('live-deactivate', {
      isBusy,
      liveOffScreen,
      scrollTop: Math.round(scrollContainer?.scrollTop ?? -1),
    });
    setLiveStartMessageId(null);
    setLiveOffScreen(false);
  }, [isBusy, liveOffScreen, liveStartMessageId, threadContainerRef, threadId]);

  const { ref: liveBufferRef, inViewport: isLiveBufferInViewport } =
    useInViewport<HTMLDivElement>();
  // Buffer stays mounted while the agent is typing or while the user can still
  // see it. Once typing stops AND the user has scrolled past it, the layout
  // collapses to just the content.
  const showLiveBuffer = isLive && (isResponding || isLiveBufferInViewport);

  // Debug: buffer unmount removes ~65% viewport height from the list end.
  const prevShowLiveBufferRef = useRef(showLiveBuffer);
  useEffect(() => {
    if (prevShowLiveBufferRef.current !== showLiveBuffer) {
      logChatRenderDebug('live-buffer-toggle', {
        show: showLiveBuffer,
        isResponding,
        isLiveBufferInViewport,
      });
      prevShowLiveBufferRef.current = showLiveBuffer;
    }
  }, [showLiveBuffer, isResponding, isLiveBufferInViewport]);

  return {
    liveStartIndex,
    isLive,
    isLiveInView,
    liveRegionRef,
    jumpToLiveRegion,
    liveBufferRef,
    showLiveBuffer,
  };
}

function getThreadScrollContainer(
  ref: React.RefObject<HTMLDivElement | null>,
  threadId?: string
): HTMLDivElement | null {
  if (!threadId) return null;
  return ref.current?.querySelector(
    `#${CHAT_THREAD_ID}-${threadId}`
  ) as HTMLDivElement | null;
}
